import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import ToolDiffSection from "../ToolDiffSection";
import { stringifyExecution, stringifyResponseSchema, stringifyToolDefinition } from "../utils";

import LoadingSpinner from "@/components/LoadingSpinner";
import BreadcrumbsPageWrapper from "@/components/UnifiedRegistry/BreadcrumbsPageWrapper";
import useFolderPathBreadcrumbs from "@/components/UnifiedRegistry/hooks/useFolderPathBreadcrumbs";
import { Button } from "@/components/ui/button";
import { useGetToolRegistry, useGetToolVersion } from "@/queries/tool-registry";

const ToolDiffPage: React.FC = () => {
  const { workspaceId, toolId, versionNumber, compareVersionNumber } = useParams();
  const navigate = useNavigate();

  const workspaceIdNum = workspaceId ? Number(workspaceId) : null;
  const toolIdNum = toolId ? Number(toolId) : null;
  const v1 = versionNumber ? Number(versionNumber) : null;
  const v2 = compareVersionNumber ? Number(compareVersionNumber) : null;

  const { data: toolData } = useGetToolRegistry(workspaceIdNum, toolIdNum);
  const tool = toolData?.tool_registry;

  const { data: version1Data, isLoading: isLoading1 } = useGetToolVersion(
    workspaceIdNum,
    toolIdNum,
    v1,
  );
  const { data: version2Data, isLoading: isLoading2 } = useGetToolVersion(
    workspaceIdNum,
    toolIdNum,
    v2,
  );

  const breadcrumbProps = useFolderPathBreadcrumbs(tool?.folder_id ?? null, tool?.name);
  const isLoading = isLoading1 || isLoading2;

  const oldJson = stringifyToolDefinition(version1Data?.version?.tool_definition);
  const newJson = stringifyToolDefinition(version2Data?.version?.tool_definition);

  const oldExecJson = stringifyExecution(version1Data?.version?.execution);
  const newExecJson = stringifyExecution(version2Data?.version?.execution);
  const executionChanged = oldExecJson !== newExecJson;

  const oldResponseSchemaJson = stringifyResponseSchema(version1Data?.version?.response_schema);
  const newResponseSchemaJson = stringifyResponseSchema(version2Data?.version?.response_schema);
  const responseSchemaChanged = oldResponseSchemaJson !== newResponseSchemaJson;

  return (
    <BreadcrumbsPageWrapper {...breadcrumbProps} className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Button
            variant="link"
            size="sm"
            className="p-0 text-sm text-gray-500 hover:text-gray-900"
            onClick={() =>
              navigate(`/workspace/${workspaceId}/tool/${toolId}/version/${versionNumber}`)
            }
          >
            {tool?.name ?? "Tool"}
          </Button>
          <span>&gt;</span>
          <span className="font-medium text-gray-900">
            Version {v1} vs Version {v2}
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <LoadingSpinner size={8} />
          </div>
        ) : (
          <div className="space-y-4 p-4">
            <ToolDiffSection
              title="Schema"
              oldValue={oldJson}
              newValue={newJson}
              leftTitle={`Version ${v1}`}
              rightTitle={`Version ${v2}`}
            />

            {responseSchemaChanged && (
              <ToolDiffSection
                title="Response schema"
                oldValue={oldResponseSchemaJson}
                newValue={newResponseSchemaJson}
                leftTitle={`Version ${v1}`}
                rightTitle={`Version ${v2}`}
              />
            )}

            {executionChanged && (
              <ToolDiffSection
                title="Function body"
                oldValue={oldExecJson}
                newValue={newExecJson}
                leftTitle={`Version ${v1}`}
                rightTitle={`Version ${v2}`}
              />
            )}
          </div>
        )}
      </div>
    </BreadcrumbsPageWrapper>
  );
};

export default ToolDiffPage;
